import { createWebSocketRequest } from './utils';

/**
 * Get the total count of available instruments
 * @returns {Promise<number>} A promise that resolves with the total number of instruments
 */
export const getInstrumentsCount = () => {
  return createWebSocketRequest({
    eventName: 'instruments-length-response',
    requestType: 'get-instruments-length',
    dataValidator: (data) => {
      if (data && typeof data.data === 'number') {
        return data.data;
      }
      return undefined;
    },
    errorMessage: 'Timeout waiting for instruments count'
  });
};

/**
 * Get instruments by index range
 * @param {number} start - Start index (0-based)
 * @param {number} count - Number of instruments to fetch
 * @param {Object} filter - Optional filter criteria for instruments
 * @returns {Promise<Array>} A promise that resolves with an array of instrument objects
 */
export const getInstrumentsByIndex = (start, count, filter = {}) => {
  // Validate parameters
  if (typeof start !== 'number' || start < 0) {
    return Promise.reject(new Error('Start index must be a non-negative number'));
  }
  
  if (typeof count !== 'number' || count <= 0) {
    return Promise.reject(new Error('Count must be a positive number'));
  }
  
  // We're now using 'get-instruments' endpoint with index-based parameters
  // This avoids the issues with 'get-ins-by-index' returning too many items
  // NOTE: The server expects parameters named 'start' and 'count' (not start_index)
  return createWebSocketRequest({
    eventName: 'get-instruments-response', // Use expected response event name
    alternativeEventNames: ['instruments', 'get-instruments-by-index-response'], // Fallback to alternative event names
    requestType: 'get-instruments', // Changed from 'get-ins-by-index' to 'get-instruments'
    requestData: {
      start: start, // Server expects 'start', not 'start_index'
      count: count,
      ...(Object.keys(filter).length > 0 ? { filter } : {})
    },
    // Use a longer timeout for larger requests to ensure we don't timeout too quickly
    timeout: count > 10 ? 45000 : 30000, 
    // Use more retries for larger requests
    maxRetries: count > 10 ? 4 : 3,
    dataValidator: (data) => {
      console.log(`Instruments response for indices ${start}-${start+count-1} using get-instruments:`, data);
      
      // Handle case where data is directly in the response
      if (data && Array.isArray(data)) {
        // Log symbols to help debug duplicate IDs
        if (data.length > 0) {
          console.log('First instrument ID/symbol:', data[0].symbol || data[0].id);
          
          // Check for duplicate symbols
          const symbols = data.map(item => item.symbol || item.id);
          const uniqueSymbols = new Set(symbols);
          if (symbols.length !== uniqueSymbols.size) {
            console.warn('⚠️ Duplicate instrument IDs detected in response:', 
              symbols.filter((s, i) => symbols.indexOf(s) !== i));
          }
          
          // Check if server returned more items than we requested
          if (data.length > count) {
            console.warn(`⚠️ Server returned ${data.length} instruments when we requested ${count}. Trimming to requested count.`);
            return data.slice(0, count);
          }
        }
        return data;
      }
      
      // Handle case where data is in data property as array
      if (data && data.data && Array.isArray(data.data)) {
        // Log symbols to help debug duplicate IDs
        if (data.data.length > 0) {
          console.log('First instrument ID/symbol:', data.data[0].symbol || data.data[0].id);
          
          // Check for duplicate symbols
          const symbols = data.data.map(item => item.symbol || item.id);
          const uniqueSymbols = new Set(symbols);
          if (symbols.length !== uniqueSymbols.size) {
            console.warn('⚠️ Duplicate instrument IDs detected in data.data:', 
              symbols.filter((s, i) => symbols.indexOf(s) !== i));
          }
          
          // Check if server returned more items than we requested
          if (data.data.length > count) {
            console.warn(`⚠️ Server returned ${data.data.length} instruments when we requested ${count}. Trimming to requested count.`);
            return data.data.slice(0, count);
          }
        }
        return data.data;
      }
      
      // Handle case where data is deeply nested
      if (data && data.instruments && Array.isArray(data.instruments)) {
        if (data.instruments.length > count) {
          console.warn(`⚠️ Server returned ${data.instruments.length} instruments when we requested ${count}. Trimming to requested count.`);
          return data.instruments.slice(0, count);
        }
        return data.instruments;
      }
      
      // If we can't parse the data, log and return empty array instead of undefined
      // to avoid crashing but allow the app to continue
      console.error('Could not parse instruments response:', data);
      return [];
    },
    errorMessage: `Timeout waiting for instruments (${start}-${start + count - 1})`
  });
};

/**
 * Get detailed information for specific instruments by their symbols
 * @param {string|string[]} symbols - Single symbol or array of symbols to fetch details for
 * @returns {Promise<Array>} A promise that resolves with the instrument details
 */
export const getInstrumentsBySymbol = (symbols) => {
  // Validate parameter
  if (!symbols) {
    return Promise.reject(new Error('No symbols provided to fetch instrument details'));
  }
  
  // Convert single symbol to array for consistent handling
  const symbolsArray = Array.isArray(symbols) ? symbols : [symbols];
  
  if (symbolsArray.length === 0) {
    return Promise.resolve([]);
  }
  
  return createWebSocketRequest({
    eventName: 'instruments',
    requestType: 'get-instruments',
    requestData: {
      symbols: symbolsArray
    },
    dataValidator: (data) => {
      if (data.data && Array.isArray(data.data)) {
        return data.data;
      }
      return undefined;
    },
    errorMessage: `Timeout waiting for instrument details for symbols: ${symbolsArray.join(', ')}`
  });
};